import React from 'react';

/**
 * Content-Bausteine für Modal-Bodies im Claude-Design-Entwurf
 * „Einheitliches Fenster-Design" – umgesetzt mit unseren Projekt-Farbtokens
 * (primary/gray, Dark-Mode), aber der Struktur/Typografie/Spacing des Entwurfs.
 */

export interface ModalSectionProps {
  /** Abschnittsüberschrift (dezent, small caps). */
  title?: string;
  /** Optionale Beschreibung unter dem Titel. */
  description?: string;
  /** Optionale Aktionen/Badge rechts neben dem Titel. */
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** Abschnitt im Modal-Body. Mehrere Sections mit `space-y-6` gruppieren. */
export const ModalSection: React.FC<ModalSectionProps> = ({
  title,
  description,
  actions,
  children,
  className = '',
}) => (
  <section className={className}>
    {(title || actions) && (
      <div className="mb-3 flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0">
          {title && (
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {title}
            </h3>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        {actions && <div className="max-w-full flex-shrink-0">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

export interface ModalRowProps {
  /** Label (links, gedämpft). */
  label: React.ReactNode;
  /** Optionales kleines Label-Icon. */
  icon?: React.ReactNode;
  /** Wert; bricht bei Bedarf um. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Key-Value-Zeile (InfoList): Desktop Grid 160px/1fr mit dezenter Trennlinie,
 * mobil gestapelt (Label über Wert).
 */
export const ModalRow: React.FC<ModalRowProps> = ({ label, icon, children, className = '' }) => (
  <div
    className={`grid grid-cols-1 gap-1 border-b border-gray-100 py-2.5 last:border-0 dark:border-gray-700/60 sm:grid-cols-[160px_1fr] sm:items-baseline sm:gap-3 ${className}`.trim()}
  >
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400">
      {icon}
      {label}
    </span>
    <span className="min-w-0 break-words text-[0.9375rem] font-medium text-gray-900 dark:text-white">
      {children}
    </span>
  </div>
);

/** Einheitliche Feld-Klassen im Entwurf-Stil (Höhe 38, Radius 8, Fokus-Grün). */
export const MODAL_INPUT =
  'h-[38px] w-full rounded-lg border border-gray-300 bg-white px-3 text-[0.9375rem] text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:cursor-not-allowed disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-500 dark:disabled:bg-gray-800';

/** Wie MODAL_INPUT, aber für mehrzeilige Felder. */
export const MODAL_TEXTAREA =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[0.9375rem] text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:cursor-not-allowed disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-500';

export interface ModalFieldProps {
  /** Feld-Label (über dem Eingabefeld). */
  label?: React.ReactNode;
  /** htmlFor-Zuordnung. */
  htmlFor?: string;
  /** Pflichtfeld-Markierung (*). */
  required?: boolean;
  /** Hinweistext (rechts neben Label, gedämpft). */
  hint?: React.ReactNode;
  /** Fehlermeldung unter dem Feld. */
  error?: React.ReactNode;
  /** Optionales Label-Icon. */
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Formularfeld (FormField): Label oben (text-sm, semibold), gap 6, optional
 * Pflicht-Stern, Hinweis und Fehlermeldung.
 */
export const ModalField: React.FC<ModalFieldProps> = ({
  label,
  htmlFor,
  required,
  hint,
  error,
  icon,
  children,
  className = '',
}) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${className}`.trim()}>
    {label && (
      <label
        htmlFor={htmlFor}
        className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-semibold tracking-tight text-gray-900 dark:text-white"
      >
        {icon}
        <span>{label}</span>
        {required && <span className="text-error-500">*</span>}
        {hint && (
          <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">{hint}</span>
        )}
      </label>
    )}
    {children}
    {error && (
      <p className="inline-flex items-center gap-1 text-xs text-error-600 dark:text-error-400">
        {error}
      </p>
    )}
  </div>
);

export default ModalSection;
